import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { format, subDays } from "date-fns";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/ui/DatePicker";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { apiGetTimekeepingReport } from "@/lib/api";
import { TimekeepingAnalytics } from "@/components/analytics/TimekeepingAnalytics";
import { downloadCsv, downloadXls, downloadPdf, type Cell } from "@/lib/report-export";
import { formatLocalizedDate } from "@/lib/localized-date";

export function ReportsPanel() {
  const { t, i18n } = useTranslation();
  const [from, setFrom] = useState(format(subDays(new Date(), 90), "yyyy-MM-dd"));
  const [to, setTo] = useState(format(new Date(), "yyyy-MM-dd"));

  const { data, isLoading } = useQuery({
    queryKey: ["timekeeping-report", from, to],
    queryFn: () => apiGetTimekeepingReport({ from, to }),
  });

  const buildRows = (): Cell[][] => {
    const header: Cell[] = ["Employee", "Department", "Total hours", "Weeks", "Approved", "Pending", "Rejected"];
    const rows: Cell[][] = (data?.employees ?? []).map((e) => [
      e.name, e.department, e.total_hours, e.weeks, e.approved_weeks, e.pending_weeks, e.rejected_weeks,
    ]);
    return [header, ...rows];
  };
  const baseName = `timekeeping-${from}_to_${to}`;
  const exportCsv = () => data && downloadCsv(`${baseName}.csv`, buildRows());
  const exportXls = () => data && downloadXls(`${baseName}.xls`, "Timekeeping", buildRows());
  const exportPdf = () => data && downloadPdf(
    `${baseName}.pdf`,
    t("timekeeping.reports.pdfTitle", { defaultValue: "Timekeeping Report" }),
    `${formatLocalizedDate(from, i18n.resolvedLanguage || i18n.language)} — ${formatLocalizedDate(to, i18n.resolvedLanguage || i18n.language)}`,
    buildRows(),
    [150, 110, 70, 50, 60, 55, 55],
  );

  return (
    <div className="space-y-5">
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <Label className="text-xs">{t("timekeeping.reports.from", { defaultValue: "From" })}</Label>
          <DatePicker value={from} onChange={setFrom} className="mt-1 h-8 w-[168px]" />
        </div>
        <div>
          <Label className="text-xs">{t("timekeeping.reports.to", { defaultValue: "To" })}</Label>
          <DatePicker value={to} onChange={setTo} className="mt-1 h-8 w-[168px]" />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="ml-auto" disabled={!data || data.employees.length === 0}>
              <Download className="h-4 w-4 mr-1" />{t("timekeeping.reports.export", { defaultValue: "Export" })}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={exportCsv}>CSV</DropdownMenuItem>
            <DropdownMenuItem onClick={exportXls}>Excel</DropdownMenuItem>
            <DropdownMenuItem onClick={exportPdf}>PDF</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <TimekeepingAnalytics data={data} isLoading={isLoading} />

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30 text-left">
                  <th className="px-4 py-2 font-medium">{t("timekeeping.reports.employee", { defaultValue: "Employee" })}</th>
                  <th className="px-4 py-2 font-medium">{t("timekeeping.reports.department", { defaultValue: "Department" })}</th>
                  <th className="px-4 py-2 font-medium text-right">{t("timekeeping.reports.totalHours", { defaultValue: "Total hours" })}</th>
                  <th className="px-4 py-2 font-medium text-right">{t("timekeeping.reports.weeks", { defaultValue: "Weeks" })}</th>
                  <th className="px-4 py-2 font-medium text-right">{t("timekeeping.status.approved", { defaultValue: "Approved" })}</th>
                  <th className="px-4 py-2 font-medium text-right">{t("timekeeping.reports.pending", { defaultValue: "Pending" })}</th>
                  <th className="px-4 py-2 font-medium text-right">{t("timekeeping.status.rejected", { defaultValue: "Rejected" })}</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr><td colSpan={7} className="px-4 py-6"><Skeleton className="h-5 w-full" /></td></tr>
                )}
                {!isLoading && (data?.employees.length ?? 0) === 0 && (
                  <tr><td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">
                    {t("timekeeping.reports.empty", { defaultValue: "No timesheet data in this range." })}
                  </td></tr>
                )}
                {data?.employees.map((e) => (
                  <tr key={e.id} className="border-b last:border-0">
                    <td className="px-4 py-2">{e.name}</td>
                    <td className="px-4 py-2 text-muted-foreground">{e.department}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-medium">{e.total_hours}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{e.weeks}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-emerald-600">{e.approved_weeks}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-blue-600">{e.pending_weeks}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-red-600">{e.rejected_weeks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
